import type { ThunkAction, UnknownAction } from '@reduxjs/toolkit'

import { httpOf, observedRequest } from './baseQuery'
import { FILE_TIMEOUT_MS, type RequestObserver, type TransportRequest, type TransportResult } from './transport'

/**
 * Файл, полученный командой. Живёт только в памяти вызова: в Redux и в кэш RTK
 * не попадает — команда отдаёт его на скачивание и забывает.
 */
export interface DownloadedFile {
  blob: Blob
  /** Имя из `Content-Disposition`, если сервер его прислал. */
  fileName: string | null
}

/**
 * Имя файла из `Content-Disposition`: `filename*=UTF-8''…` (RFC 5987, им ASP.NET
 * передаёт кириллицу) важнее простого `filename=`.
 */
export function fileNameOf(disposition: string | undefined): string | null {
  if (disposition === undefined) return null
  const extended = /filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i.exec(disposition)
  if (extended?.[2] !== undefined) {
    try {
      return decodeURIComponent(extended[2].trim().replace(/^"(.*)"$/, '$1'))
    } catch {
      // Битое кодирование — берём простое имя ниже.
    }
  }
  const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/i.exec(disposition)
  const name = plain?.[2] ?? plain?.[1]
  return name === undefined || name.trim() === '' ? null : name.trim()
}

type TransportThunk<T> = ThunkAction<Promise<TransportResult<T>>, unknown, unknown, UnknownAction>

/**
 * Запрос через transport стора МИМО кэша RTK: `dispatch(transportRequest(...))`.
 * Для команд, аргументы которых нельзя класть в Redux (а RTK хранит аргументы
 * мутации в состоянии): загрузка снимка несёт `File` в `formData`. Правила те же,
 * что у endpoint: CSRF, корреляция, без повтора команды, `unknown-outcome`.
 */
export function transportRequest<T = unknown>(
  request: TransportRequest,
  signal?: AbortSignal,
  observer?: RequestObserver,
): TransportThunk<T> {
  return (dispatch, _getState, extra) => observedRequest<T>(httpOf(extra), request, signal, dispatch, observer)
}

/**
 * Команда «получить файл» через тот же transport (cookie, корреляция, разбор
 * отказа, правила повтора), но мимо кэша RTK: `dispatch(requestFile({ url }))`.
 * Метод по умолчанию GET; `responseType` принудительно `blob`, таймаут по
 * умолчанию — `FILE_TIMEOUT_MS`.
 */
export function requestFile(
  request: Omit<TransportRequest, 'responseType' | 'decode'>,
  signal?: AbortSignal,
): TransportThunk<DownloadedFile> {
  return async (dispatch, _getState, extra) => {
    const result = await observedRequest<Blob>(
      httpOf(extra),
      { timeoutMs: FILE_TIMEOUT_MS, ...request, responseType: 'blob' },
      signal,
      dispatch,
    )
    if (!result.ok) return result
    return {
      ...result,
      data: { blob: result.data, fileName: fileNameOf(result.headers?.['content-disposition']) },
    }
  }
}

/**
 * Сколько живёт ссылка на объект после щелчка, мс. Firefox, Safari на iOS и
 * часть WebView начинают читать файл позже щелчка: отозванная сразу ссылка даёт
 * пустой или отменённый файл (та же задержка — у FileSaver.js).
 */
const OBJECT_URL_LIFETIME_MS = 40_000

/** Отдать файл браузеру на сохранение. */
export function saveFile(file: DownloadedFile, fallbackName: string): void {
  const url = URL.createObjectURL(file.blob)
  try {
    const link = document.createElement('a')
    link.href = url
    link.download = file.fileName ?? fallbackName
    link.rel = 'noopener'
    document.body.append(link)
    link.click()
    link.remove()
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_LIFETIME_MS)
  }
}
